import './SelectInput.scss';

import { mdiClose } from '@mdi/js';
import { Select } from 'antd';
import type { LabeledValue } from 'common/models';
import Icon from 'components/Icon/Icon';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useMemo } from 'react';
import type { ControllerRenderProps, FieldValues, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import type { InputNoteConfig } from '../_common';
import {
  getHookFieldClass,
  getHookGroupClass,
  renderErrorMessage,
  renderHookInputHeader
} from '../_common';
import type { SelectItemWithDescription } from './SelectInput';

interface AsyncSelectInputProps {
  label?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  inputNote?: InputNoteConfig;
  options?: RegisterOptions;
  popupRootClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  noOptionsMessage?: string;
  popupRender?: (menu: React.ReactElement) => React.ReactElement;
  mode?: 'multiple' | 'tags';
  customOnChange?: (value: LabeledValue) => void;
  onSearch: (value: string) => void;
  items: SelectItemWithDescription[];
  hideLabel?: boolean;
}

const { Option } = Select;

const AsyncSelectInput = ({
  name,
  label,
  inputNote,
  required,
  disabled,
  loading,
  options,
  placeholder,
  popupRootClassName,
  popupRender,
  noOptionsMessage,
  mode,
  customOnChange,
  items,
  onSearch,
  hideLabel
}: AsyncSelectInputProps) => {
  const { t } = useCustomTranslation();
  const { control, getFieldState, formState } = useFormContext();

  const { invalid, isDirty, error, isTouched } = getFieldState(name, formState);

  const showError = !!error && isDirty;

  const [searchValue, setSearchValue] = React.useState('');

  const mappedOptions = useMemo(
    () =>
      items.map((d: SelectItemWithDescription) => (
        <Option title={d.name} key={String(d.id)} value={d.id} label={d.name}>
          <div className="Option_name">{d.name}</div>
          <div className="Option_description">{d.description}</div>
        </Option>
      )),
    [items]
  );

  const customTagRender = (tagProps: any) => {
    const { label, closable, onClose } = tagProps;

    return (
      <span className="ant-select-selection-item">
        <span className="ant-select-selection-item-content">{label}</span>
        <span className="ant-select-selection-item-remove" aria-hidden="true">
          <span role="img" aria-label="close" className="anticon anticon-close">
            <Icon path={mdiClose} onClick={closable && onClose} />
          </span>
        </span>
      </span>
    );
  };

  const handleChange = (
    field: ControllerRenderProps<FieldValues, string>,
    value?: { value: string; label: React.ReactNode } | { value: string; label: React.ReactNode }[]
  ) => {
    if (Array.isArray(value)) {
      return field.onChange(
        value.map((option) => ({
          value: option.value?.toString() || '',
          label: option.label?.toString() || ''
        }))
      );
    }

    if (value) {
      return field.onChange({
        value: value.value?.toString() || '',
        label: value.label?.toString() || ''
      });
    }
  };

  // Update the input immediately; debounce only the async search call to avoid UI lag
  const debouncedSearch = useDebouncedCallback(onSearch, 500);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const resolvedPopupClassName = popupRootClassName;

  return (
    <div className={getHookGroupClass(label, 'HookAsyncSelectInput')}>
      {!hideLabel && renderHookInputHeader(name, label, required, inputNote)}
      <Controller
        name={name}
        control={control}
        rules={options}
        render={({ field }) => {
          // If the field value is null/undefined, pass undefined to Select to fully clear UI.
          // Using null in RHF avoids snapping back to defaultValue when clearing.
          const selectValue =
            field.value === null || field.value === undefined ? undefined : field.value;

          return (
            <Select
              {...field}
              value={selectValue}
              onChange={(value) => {
                setSearchValue(''); // clear search after select
                if (customOnChange && !Array.isArray(value)) {
                  customOnChange({
                    value: value.value?.toString() || '',
                    label: value.label?.toString() || ''
                  });
                }
                handleChange(field, value);
              }}
              id={name}
              placeholder={placeholder}
              labelInValue
              className={getHookFieldClass(isTouched, invalid)}
              optionLabelProp="label"
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
              classNames={{ popup: { root: resolvedPopupClassName || 'HookSelectInputDropdown' } }}
              disabled={disabled}
              loading={loading}
              mode={mode}
              allowClear={!required}
              onClear={() => {
                setSearchValue(''); // clear search after clear
                field.onChange(null); // keep RHF value cleared even when defaultValue is set
              }}
              showSearch={{ filterOption: false, onSearch: handleSearch, searchValue }}
              autoClearSearchValue
              tagRender={customTagRender}
              popupRender={popupRender}
              notFoundContent={noOptionsMessage || t('common.select.noResults')}
              onOpenChange={(open) => {
                if (!open) handleSearch(''); // this will handle the select will open with all options on another opening
              }}
            >
              {mappedOptions}
            </Select>
          );
        }}
      />
      {renderErrorMessage(showError, error?.message)}
    </div>
  );
};

export default AsyncSelectInput;
